import { bookendFonts } from "./font.js";
import { NEUTRAL_CHROMA, paletteChroma, paletteLuminance } from "./palette.js";

/**
 * Reading a brand off a company's own website.
 *
 * Deliberately pure, and deliberately separate from the fetching. Everything
 * hard about this is the guessing — which of forty colours on a page is the
 * accent, whether a font name is one we can actually render — and none of it
 * needs a network to test. The route that calls this does the fetching and the
 * refusing; this only reads text.
 *
 * It is a guess and is presented to the user as one. A brand kit assembled from
 * a homepage is right often enough to save somebody typing six hex codes, and
 * wrong often enough that it must land in a form they can correct rather than
 * being saved for them.
 */
export interface BrandCandidates {
  /** The site's own name for itself, from <title> or og:site_name. */
  name: string | null;
  /** Absolute URLs, best first. */
  logos: string[];
  /** Every colour found, in the order met, duplicates kept — frequency matters. */
  colours: string[];
  /** The colour the site itself nominated, if it did. */
  declared: string | null;
  /** Font family names as written, before matching against what we can render. */
  fonts: string[];
}

/** `#abc` is the same colour as `#aabbcc`, and both turn up on one page. */
function expandHex(value: string): string | null {
  const hex = value.replace("#", "").toLowerCase();
  if (hex.length === 3) return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  // 8-digit hex carries alpha, which a brand colour does not.
  if (hex.length === 6 || hex.length === 8) return `#${hex.slice(0, 6)}`;
  return null;
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[r, g, b].map((n) => clamp(n).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Every colour in a blob of CSS or markup.
 *
 * Both notations, because a page written by hand and a page built by a
 * framework rarely agree on which to use, and taking only one loses half the
 * evidence on any given site.
 */
export function coloursIn(text: string): string[] {
  const found: string[] = [];

  for (const match of text.matchAll(/#[0-9a-fA-F]{3,8}\b/g)) {
    const hex = expandHex(match[0]);
    if (hex) found.push(hex);
  }

  for (const match of text.matchAll(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/g)) {
    const [r, g, b] = [match[1], match[2], match[3]].map(Number);
    if ([r, g, b].every((n) => Number.isFinite(n))) found.push(rgbToHex(r, g, b));
  }

  return found;
}

const META = (html: string, attr: string, value: string): string | null => {
  // Both attribute orders, because either is valid and both are common.
  const patterns = [
    new RegExp(`<meta[^>]+${attr}=["']${value}["'][^>]*content=["']([^"']+)["']`, "i"),
    new RegExp(`<meta[^>]+content=["']([^"']+)["'][^>]*${attr}=["']${value}["']`, "i"),
  ];
  for (const pattern of patterns) {
    const match = pattern.exec(html);
    if (match) return match[1].trim();
  }
  return null;
};

function linkHrefs(html: string, rel: RegExp): string[] {
  const out: string[] = [];
  for (const tag of html.matchAll(/<link\b[^>]*>/gi)) {
    const relAttr = /rel=["']([^"']+)["']/i.exec(tag[0])?.[1];
    const href = /href=["']([^"']+)["']/i.exec(tag[0])?.[1];
    if (relAttr && href && rel.test(relAttr)) out.push(href);
  }
  return out;
}

/**
 * The site's own logo, as drawn in its page.
 *
 * Most sites put their mark in the header as an `<img>` — dashrendr.com's is
 * `<img alt="dashrendr" src="/dashrendr-logo.png">` — and no icon tag points
 * at it. Without this the best candidate on such a site was its og:image, a
 * 1200×630 social card, which a film then drew as a "logo".
 *
 * Two kinds, in this order. An image that names the site and says it is its
 * logo, or whose alt text is the site's name: the strongest sign there is, and it rules out a customer's logo
 * in a "trusted by" strip, which names somebody else. Then an image called a
 * logo inside the page's header or nav, where only the site's own mark sits.
 */
function pageLogoSrcs(html: string, baseUrl: string, name: string | null): string[] {
  const plain = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const host = (() => {
    try {
      return new URL(baseUrl).hostname.replace(/^www\./, "").split(".")[0];
    } catch {
      return "";
    }
  })();
  const own = [plain(host), plain(name?.split(/[|–—:-]/)[0] ?? "")].filter((word) => word.length >= 3);

  const imgs = (part: string) =>
    [...part.matchAll(/<img\b[^>]*>/gi)].slice(0, 60).flatMap((tag) => {
      const attr = (key: string) => new RegExp(`\\s${key}=["']([^"']*)["']`, "i").exec(tag[0])?.[1] ?? "";
      const src = attr("src") || attr("data-src");
      if (!src || src.startsWith("data:")) return [];
      return [
        {
          src,
          alt: plain(attr("alt")),
          said: plain(`${attr("alt")} ${src.split("/").pop() ?? ""}`),
          attrs: plain(tag[0]),
        },
      ];
    });

  // Naming the site is not enough on its own — "dashrendr-hero.png" is a
  // screenshot — so it must also say it is a mark, or be alt text that is the
  // name and nothing else.
  const named = imgs(html).filter((img) =>
    own.some((word) => img.alt === word || (img.said.includes(word) && /logo|mark|brand/.test(img.said))),
  );
  const chrome = [...html.matchAll(/<(header|nav)\b[\s\S]*?<\/\1>/gi)].map((match) => match[0]).join("\n");
  const headed = imgs(chrome).filter((img) => img.attrs.includes("logo"));
  return [...named, ...headed].map((img) => img.src);
}

/**
 * HTML entities in an attribute value, decoded.
 *
 * A URL written in markup is escaped, and `?w=180&amp;h=180` is a perfectly
 * ordinary thing to find in an `<img src>` — Stripe's homepage serves exactly
 * that. Left encoded it is not the address the site meant, and the fetch
 * either 404s or comes back with something that is not an image.
 */
export function decodeEntities(value: string): string {
  return (
    value
      .replace(/&(?:#(\d+)|#[xX]([0-9a-fA-F]+)|(amp|lt|gt|quot|apos|nbsp|#39));/g, (whole, dec, hex, named) => {
        if (dec) return String.fromCodePoint(Number(dec));
        if (hex) return String.fromCodePoint(parseInt(hex, 16));
        switch (named) {
          case "amp":
            return "&";
          case "lt":
            return "<";
          case "gt":
            return ">";
          case "quot":
            return '"';
          case "apos":
          case "#39":
            return "'";
          case "nbsp":
            return " ";
          default:
            return whole;
        }
      })
      // `&amp;amp;` happens when a template escapes twice, and one pass leaves
      // `&amp;` behind — still not the address the site meant.
      .replace(/&amp;/g, "&")
  );
}

/**
 * The size an icon claims, from its own filename.
 *
 * `apple-touch-icon-57x57.png` and `apple-touch-icon-180.png` sit in the same
 * `<head>` in document order, and taking the first gave Vercel a 57-pixel icon
 * to put in a 1920-pixel frame. Unsized wins, because a bare
 * `apple-touch-icon.png` is a 180 by convention.
 */
export function iconSize(href: string): number {
  const pair = /(\d{2,4})\s*[xX]\s*(\d{2,4})/.exec(href);
  if (pair) return Math.min(Number(pair[1]), Number(pair[2]));
  const single = /[-_](\d{2,4})\.(png|jpe?g|webp|svg)/i.exec(href);
  if (single) return Number(single[1]);
  return 180;
}

const absolute = (href: string, base: string): string | null => {
  try {
    return new URL(href, base).toString();
  } catch {
    return null;
  }
};

/**
 * Names that are not a typeface.
 *
 * Every real font stack ends in several of these, and counting them produced
 * "49 typefaces" off one page — a number that says nothing, in a list where
 * the actual face was buried. The system-UI aliases go too: they name whatever
 * the reader's machine has, which is the opposite of a brand.
 */
const GENERIC_FAMILIES = new Set(
  [
    "serif",
    "sans-serif",
    "monospace",
    "cursive",
    "fantasy",
    "system-ui",
    "math",
    "emoji",
    "fangsong",
    "ui-sans-serif",
    "ui-serif",
    "ui-monospace",
    "ui-rounded",
    "-apple-system",
    "blinkmacsystemfont",
    "segoe ui",
    "roboto",
    "helvetica",
    "helvetica neue",
    "arial",
    "apple color emoji",
    "segoe ui emoji",
    "segoe ui symbol",
    "noto color emoji",
    "inherit",
    "initial",
    "unset",
    "revert",
    "none",
  ].map((name) => name.toLowerCase()),
);

/** One declaration's worth of families, cleaned. */
function splitFamilies(value: string, vars: Map<string, string>): string[] {
  // Sites indirect through custom properties — `font-family: var(--font-sans)`
  // — so the declaration alone reads as the literal text "var(--font-sans)".
  // Resolved once, which is enough: a variable defined in terms of another is
  // rare and not worth chasing round a stylesheet.
  const resolved = value.replace(
    /var\(\s*(--[\w-]+)\s*(?:,[^)]*)?\)/g,
    (whole, name: string) => vars.get(name) ?? whole,
  );

  return resolved
    .split(",")
    .map((family) =>
      family
        .trim()
        .replace(/^["']|["']$/g, "")
        .trim(),
    )
    .filter((family) => family.length > 0 && family.length < 60)
    .filter((family) => !family.startsWith("var("))
    .filter((family) => !GENERIC_FAMILIES.has(family.toLowerCase()));
}

/**
 * Every typeface a page names, best evidence first.
 *
 * `@font-face` leads because it is the only one that is not a preference: a
 * site does not ship a font file it does not use, whereas a `font-family`
 * declaration lists four fallbacks it hopes never to reach.
 */
export function familiesIn(css: string, stylesheetHrefs: readonly string[] = []): string[] {
  const vars = new Map<string, string>();
  for (const match of css.matchAll(/(--[\w-]+)\s*:\s*([^;}]+)/g)) {
    const name = match[1];
    const value = match[2].trim();
    // Named like a family, and holding something that could be one. The name
    // test alone was not enough: --font-size matches /font/, so `14px` was
    // being substituted into a stack and counted as a typeface.
    const namedLikeAFamily = /font|family|type|heading|display|body|sans|serif|mono/i.test(name);
    const looksLikeAMeasurement = /^[\d.]+(px|rem|em|%|vh|vw|pt|ch)?$/i.test(value) || /^[\d.]+\s/.test(value);
    if (namedLikeAFamily && !looksLikeAMeasurement) vars.set(name, value);
  }

  const found: string[] = [];
  const add = (family: string) => {
    if (!found.some((existing) => existing.toLowerCase() === family.toLowerCase())) found.push(family);
  };

  for (const block of css.matchAll(/@font-face\s*\{([^}]*)\}/gi)) {
    const family = /font-family\s*:\s*([^;}]+)/i.exec(block[1])?.[1];
    if (family) for (const name of splitFamilies(family, vars)) add(name);
  }

  // Google Fonts, which is how most sites load a face — and the name is in the
  // href even when the stylesheet itself was never fetched.
  for (const href of stylesheetHrefs) {
    for (const match of href.matchAll(/[?&]family=([^&:]+)/g)) {
      const cleaned = decodeURIComponent(match[1]).replace(/\+/g, " ").trim();
      if (cleaned) for (const name of splitFamilies(cleaned, vars)) add(name);
    }
  }

  for (const match of css.matchAll(/font-family\s*:\s*([^;}]+)/gi)) {
    for (const name of splitFamilies(match[1], vars)) add(name);
  }

  return found;
}

/**
 * What a homepage says about itself.
 *
 * `css` is any stylesheet text already fetched alongside the HTML. It is
 * optional because a site that declares a theme-color and ships inline styles
 * gives enough to work with, and fetching every stylesheet to improve a guess
 * is a lot of somebody else's bandwidth.
 */
export function brandCandidates(html: string, baseUrl: string, css = ""): BrandCandidates {
  const name =
    META(html, "property", "og:site_name") ?? /<title[^>]*>([^<]+)<\/title>/i.exec(html)?.[1]?.trim() ?? null;

  // Best first. The page's own header logo is the mark itself; apple-touch-icon is square, large and meant to sit on a
  // coloured tile, which is closer to a logo than a 16px favicon; og:image is
  // usually a social card, which is often the logo and sometimes a screenshot.
  const logoHrefs = [
    // The mark the page itself shows, when it can be told apart from others.
    ...pageLogoSrcs(html, baseUrl, name),
    // Largest first within the group. Document order is not size order, and
    // the smallest icon on the page is never the best logo.
    ...linkHrefs(html, /apple-touch-icon/i).sort((a, b) => iconSize(b) - iconSize(a)),
    META(html, "property", "og:image") ?? "",
    ...linkHrefs(html, /^(shortcut )?icon$|\bicon\b/i).sort((a, b) => iconSize(b) - iconSize(a)),
  ].filter(Boolean);

  const logos: string[] = [];
  for (const href of logoHrefs) {
    const url = absolute(decodeEntities(href), baseUrl);
    if (url && !logos.includes(url)) logos.push(url);
  }

  const declaredRaw = META(html, "name", "theme-color");
  const declared = declaredRaw ? (coloursIn(declaredRaw)[0] ?? null) : null;

  // Only the parts of the HTML that are actually styling. Running the colour
  // scan over the whole document turns any hex-like string in the copy — an
  // order number, a git sha — into a brand colour.
  const styleBlocks = [...html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)].map((m) => m[1]).join("\n");
  const inlineStyles = [...html.matchAll(/style=["']([^"']+)["']/gi)].map((m) => m[1]).join(";");

  const colours = coloursIn([styleBlocks, inlineStyles, css].join("\n"));

  const styling = [styleBlocks, inlineStyles, css].join("\n");
  const fonts = familiesIn(styling, linkHrefs(html, /stylesheet/i));

  return { name, logos, colours, declared, fonts };
}

/**
 * The closest face we ship to one we do not.
 *
 * Most sites turn out to use a typeface we cannot render — Geist, Matter,
 * NotionInter, a licensed cut — so exact matching alone answers "nothing
 * found" on nearly every real page and quietly leaves Inter in place. Falling
 * back by category is better than falling back to the default: a site set in
 * Georgia gets a serif, and a site set in Source Code Pro gets a mono, which
 * is much nearer their brand than a neutral sans.
 *
 * Classified from the name, because that is all we have without downloading
 * and parsing the font file. It is a guess, and what is done with it says so.
 */
function categoryOf(family: string): "Serif" | "Mono" | "Sans" {
  const name = family.toLowerCase();
  if (/\b(mono|code|courier|consol|typewriter)\b/.test(name)) return "Mono";
  // "sans-serif" is already filtered out; this is a real serif name.
  // No closing word boundary: these names are routinely pluralised or suffixed
  // — "Tiempos Text", "Timesnewer" — and \b(tiempo)\b matched neither.
  if (
    /\b(serif|georgia|times|garamond|baskerville|didot|bodoni|caslon|playfair|lyon|charter|freight|tiempo|newsreader|canela|domaine)/.test(
      name,
    )
  ) {
    return "Serif";
  }
  return "Sans";
}

export function nearestFont(family: string): string | null {
  const exact = matchFont(family);
  if (exact) return exact;

  const wanted = categoryOf(family);
  // The first of that category in the library, which is the one it leads with.
  return bookendFonts.find((font) => font.category === wanted)?.family ?? null;
}

/** A family we can actually render, or null. Matched loosely: sites write "Inter Tight". */
export function matchFont(family: string): string | null {
  const wanted = family.trim().toLowerCase();
  if (!wanted) return null;
  const exact = bookendFonts.find((font) => font.family.toLowerCase() === wanted);
  if (exact) return exact.family;
  const partial = bookendFonts.find(
    (font) => wanted.startsWith(font.family.toLowerCase()) || font.family.toLowerCase().startsWith(wanted),
  );
  return partial?.family ?? null;
}

export interface ScrapedBrand {
  name: string | null;
  logo: string | null;
  colors: { text: string; background: string; accent: string; accentAlt: string };
  titleFont: string | null;
  bodyFont: string | null;
  /**
   * The typeface the site actually uses, and whether we can render it.
   *
   * Reported rather than swallowed. "We cannot render Geist, so we picked
   * Inter" is a useful thing to be told; silently substituting and saying
   * nothing is how somebody ends up wondering why their videos are not in
   * their font.
   */
  typeface: { name: string; rendered: boolean } | null;
}

/**
 * Candidates reduced to one kit.
 *
 * The rules, in the order they matter:
 *
 * The background is the colour the page is mostly made of, so it is the most
 * frequent near-neutral — counting rather than picking the first, because the
 * first colour in a stylesheet is usually a reset.
 *
 * The accent is the most colourful thing on the page, not the most frequent:
 * a brand colour appears on a handful of buttons while grey appears everywhere.
 * A theme-color the site declared beats any of that, because it is the one
 * colour the site was asked to name and did.
 *
 * The text colour is chosen for contrast against the background rather than
 * scraped, because the most frequent dark colour on a page is frequently a
 * border, and text that fails to contrast is the one mistake here that makes
 * the output unusable rather than merely wrong.
 */
export function brandFromCandidates(candidates: BrandCandidates): ScrapedBrand {
  const counts = new Map<string, number>();
  for (const colour of candidates.colours) counts.set(colour, (counts.get(colour) ?? 0) + 1);

  const distinct = [...counts.keys()];
  const byFrequency = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([colour]) => colour);

  // The same threshold the palette module uses to decide a strip has no colour
  // worth leading with. Calibrated against real values rather than guessed:
  // #1f2933 scores 0.078 and #5e6ad2 scores 0.455, so the line sits between.
  const neutral = (colour: string) => paletteChroma(colour) < NEUTRAL_CHROMA;

  /**
   * theme-color is not the brand colour.
   *
   * It is the colour the browser paints its own chrome, which on most sites is
   * the page background — Linear declares #08090a and PostHog #e5e7e0, both
   * near-neutral. Taking it as the accent produced a brand kit whose accent was
   * near-black, which is how this was found.
   *
   * So it is read for what it is. Colourful, and the site has named its accent
   * and meant it. Neutral, and the site has named its background, which is more
   * reliable than counting stylesheet colours and is used for that instead.
   */
  const declaredIsAccent = candidates.declared !== null && !neutral(candidates.declared);

  const neutrals = byFrequency.filter(neutral);
  /**
   * A page background is nearly always at one end of the range. The most
   * frequent neutral on a busy stylesheet is as likely to be a border or a
   * muted label — Linear's was #9c9da1, a mid grey that is nothing's
   * background — so the extremes are preferred, and frequency only decides
   * between them.
   */
  const extreme = (colour: string) => {
    const lum = paletteLuminance(colour);
    return lum > 0.85 || lum < 0.15;
  };

  const background =
    (!declaredIsAccent ? candidates.declared : null) ??
    neutrals.find(extreme) ??
    neutrals[0] ??
    byFrequency[0] ??
    "#0b1020";

  // Against the background we chose, not against a guess about it.
  const light = paletteLuminance(background) > 0.5;
  const text = light ? "#10151c" : "#f8fafc";

  /**
   * Ranked by how often the site uses it, not by how saturated it is.
   *
   * Strongest-first picked #00ff00 off PostHog and #ffff00 off Notion — pure
   * primaries that live in an illustration or an SVG somewhere and are used
   * once. A brand colour is not the loudest thing on a page; it is the
   * colourful thing that keeps appearing, because it is on every button.
   * Chroma only breaks ties between colours used equally often.
   */
  const colourful = distinct
    .filter((colour) => !neutral(colour))
    .sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || paletteChroma(b) - paletteChroma(a));

  const accent = (declaredIsAccent ? candidates.declared : null) ?? colourful[0] ?? "#ff6900";

  // Visibly different from the accent, or the two read as one colour and the
  // second is wasted. Both the preference and the fallback exclude the accent
  // itself: the accent is often the declared colour and also the most colourful
  // thing found, so a fallback that only took "the next one in the list" could
  // hand back the accent again.
  const others = colourful.filter((colour) => colour !== accent);
  const accentAlt =
    others.find((colour) => Math.abs(paletteLuminance(colour) - paletteLuminance(accent)) > 0.12) ??
    others[0] ??
    "#38bdf8";

  /**
   * A monospace face is not a brand's typeface.
   *
   * It is what the code samples are set in, and `@font-face` blocks routinely
   * declare it first — which handed the title of a PostHog card to Source Code
   * Pro and a Vercel one to Geist Mono. So the text faces are considered
   * first, and a mono is only reached for if a page names nothing else.
   */
  const textFirst = [
    ...candidates.fonts.filter((family) => categoryOf(family) !== "Mono"),
    ...candidates.fonts.filter((family) => categoryOf(family) === "Mono"),
  ];

  // Exact before nearest, so a site that does use a face we ship gets that face
  // rather than its category's leader.
  const exact = textFirst.map(matchFont).filter((family): family is string => family !== null);
  const near = textFirst.map(nearestFont).filter((family): family is string => family !== null);

  // Named for what the site sets its words in, which is the first non-mono.
  const first = textFirst[0];
  const typeface = first ? { name: first, rendered: matchFont(first) !== null } : null;

  return {
    name: candidates.name,
    logo: candidates.logos[0] ?? null,
    colors: { text, background, accent, accentAlt },
    titleFont: exact[0] ?? near[0] ?? null,
    /**
     * A second face only when the site demonstrably has one.
     *
     * A page names a dozen families, several of them incidental, and mapping
     * each to its category's leader made every site come back with a serif
     * body it does not have — the second name in the list was some stray, and
     * a category guess on top of an incidental name is a guess on a guess.
     *
     * So a second face has to be an exact match. Otherwise the body is set in
     * the title face, which is what a site using one typeface actually does.
     */
    bodyFont: exact[1] ?? exact[0] ?? near[0] ?? null,
    typeface,
  };
}
